import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isEqual } from 'lodash';
import { useViewport } from '@fairygodboss/ui';
import { MobileFilter } from './MobileFilter';
import { DesktopRender } from './Filters/DesktopRender';
import { TabletRender } from './Filters/TabletRender';

export const JobsSearchFilter = ({
    filterJobs,
    activeFilters,
    onClearFilters,
    setFilterByType,
    locationList,
    companyList,
    isAuthenticatedUser,
    hasRemote,
}) => {
    const [activeCompanies, setActiveCompanies] = useState([]);
    const [companiesOpen, isCompaniesOpen] = useState(false);
    const [activeLocations, setActiveLocations] = useState([]);
    const [locationsOpen, isLocationsOpen] = useState(false);
    const [remoteSelected, setRemoteSelected] = useState(
        !!activeFilters?.remote?.length
    );
    const companiesFilterRef = useRef();
    const locationFiltersRef = useRef();

    const setLocationOpen = useCallback(
        (open) => {
            isLocationsOpen(open);
            if (open && companiesOpen) {
                companiesFilterRef?.current?.setFilter();
                isCompaniesOpen(false);
            }
        },
        [companiesOpen, isLocationsOpen]
    );

    const setCompaniesOpen = useCallback(
        (open) => {
            isCompaniesOpen(open);
            if (open && locationsOpen) {
                locationFiltersRef?.current?.setFilter();
                isLocationsOpen(false);
            }
        },
        [locationsOpen, isCompaniesOpen]
    );

    useEffect(() => {
        const _isSelected = !!activeFilters?.remote?.length;
        if (remoteSelected !== _isSelected) {
            setRemoteSelected(_isSelected);
        }
    }, [activeFilters, remoteSelected]);

    const { isSM, isXS } = useViewport();

    useEffect(() => {
        if (activeFilters?.companies?.length === activeCompanies?.length) {
            return;
        }
        const companyMap = {};
        companyList?.forEach((i) => {
            companyMap[i.value] = i;
        });

        const _activeCompanies = activeFilters?.company_id
            ?.map((i) => {
                if (!companyMap[i]) {
                    return;
                }
                return {
                    label: companyMap[i].displayName,
                    value: companyMap[i].value,
                };
            })
            .filter((i) => !!i);

        if (!isEqual(_activeCompanies, activeCompanies)) {
            setActiveCompanies(_activeCompanies);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters, companyList]);

    useEffect(() => {
        if (activeFilters?.locations?.length === activeLocations?.length) {
            return;
        }
        const locationMap = {};
        locationList?.forEach((i) => {
            locationMap[i.value] = i;
        });

        const _activeLocations = activeFilters.locations
            ?.map((i) => {
                if (!locationMap[i]) {
                    return;
                }
                return {
                    label: locationMap[i].displayName,
                    value: locationMap[i].value,
                };
            })
            .filter((i) => !!i);
        if (!isEqual(_activeLocations, activeLocations)) {
            setActiveLocations(_activeLocations);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters, locationList]);

    const onCompanySelect = useCallback(
        (value) => {
            // resetLocations.current = true;
            const type = 'company_id';
            setActiveCompanies(value);
            const filterArray = [];
            value?.forEach((v) => filterArray.push(v.value));
            const _activeCompanies = activeCompanies?.map((c) => c.value);
            if (!isEqual(filterArray.sort(), _activeCompanies.sort())) {
                setFilterByType({ type, values: filterArray });
            }
        },
        [setFilterByType, activeCompanies]
    );

    const onLocationSelect = useCallback(
        (value) => {
            // resetCompanies.current = true;
            setActiveLocations(value);
            const type = 'locations';
            const filterArray = [];
            value?.forEach((v) => filterArray.push(v.value));
            const _activeLocations = activeLocations?.map((c) => c.value);
            if (!isEqual(filterArray.sort(), _activeLocations.sort())) {
                setFilterByType({ type, values: filterArray });
            }
        },
        [setFilterByType, activeLocations]
    );

    const onRemoteSelect = useCallback(() => {
        const type = 'remote';
        filterJobs({
            type,
            filter: { displayName: 'Remote', value: 1 },
        });
    }, [filterJobs]);

    const bodyRef = useRef(document.querySelector('body'));

    if (isSM) {
        return (
            <DesktopRender
                ref={{
                    companiesFilterRef,
                    locationFiltersRef,
                }}
                bodyRef={bodyRef}
                remoteSelected={remoteSelected}
                onRemoteSelect={onRemoteSelect}
                locationsOpen={locationsOpen}
                isLocationsOpen={setLocationOpen}
                locations={locationList}
                onLocationSelect={onLocationSelect}
                hasRemote={hasRemote}
                activeLocations={activeLocations}
                onClearFilters={onClearFilters}
                setActiveCompanies={setActiveCompanies}
                activeCompanies={activeCompanies}
                isSM={isSM}
                setActiveLocations={setActiveLocations}
                isAuthenticatedUser={isAuthenticatedUser}
                companiesOpen={companiesOpen}
                companies={companyList}
                isCompaniesOpen={setCompaniesOpen}
                onCompanySelect={onCompanySelect}
            />
        );
    }

    if (!isXS) {
        return (
            <MobileFilter
                hasRemote={hasRemote}
                remoteSelected={remoteSelected}
                onRemoteSelect={onRemoteSelect}
                companies={companyList}
                locations={locationList}
                activeCompanies={activeCompanies}
                activeLocations={activeLocations}
                onClearFilters={onClearFilters}
                onLocationSelect={onLocationSelect}
                onCompanySelect={onCompanySelect}
                isAuthenticatedUser={isAuthenticatedUser}
            />
        );
    }

    return (
        <TabletRender
            ref={{
                companiesFilterRef,
                locationFiltersRef,
            }}
            bodyRef={bodyRef}
            remoteSelected={remoteSelected}
            onRemoteSelect={onRemoteSelect}
            locationsOpen={locationsOpen}
            isLocationsOpen={setLocationOpen}
            locations={locationList}
            onLocationSelect={onLocationSelect}
            hasRemote={hasRemote}
            activeLocations={activeLocations}
            onClearFilters={onClearFilters}
            setActiveCompanies={setActiveCompanies}
            activeCompanies={activeCompanies}
            isSM={isSM}
            setActiveLocations={setActiveLocations}
            isAuthenticatedUser={isAuthenticatedUser}
            companiesOpen={companiesOpen}
            companies={companyList}
            isCompaniesOpen={setCompaniesOpen}
            onCompanySelect={onCompanySelect}
        />
    );
};
